import React from 'react'
import { createPortal } from 'react-dom'
import { CardAccount, ValidationError, api } from '../api/client'
import type { Package, PaymentMethod, Purchase, SavedCard } from '../types/app'
import { formatPrice, priceOrFree } from '../catalog/format'
import { PAYMENT_METHODS } from '../payments/methods'
import { canTakeCardPayments } from '../payments/visibility'
import { saveErrorMessage } from '../screens/settings/toast'
import { useDialog } from '../hooks/useDialog'
import { SheetKeys } from '../payments/idempotency'
import Field from '../screens/settings/Field'
import PayLinkSheet from './PayLinkSheet'
import ChargeCardSheet from './ChargeCardSheet'
import { cardLabel, usableCard } from '../payments/savedCard'
import './Sheet.css'
import '../screens/settings/Settings.css'
import { Loading } from '../ui/States'
import { carryOverOpen } from '../packages/display'

interface SellPackageSheetProps {
  clientId: string
  clientName: string
  // The client's saved card, when they have one.
  card?: SavedCard | null
  // Whether the client has an email address (a card charge's receipt goes there).
  clientHasEmail?: boolean
  // Until when she can carry packages over from Fresha (/v1/me's
  // fresha_carry_over_until): the 60 days after her Fresha import. Null or
  // past hides the switch. The sheet asks /v1/me again as it opens, and goes
  // by this until that answers.
  carryOverUntil?: string | null
  onClose: () => void
  onSold: (purchase: Purchase, carriedOver?: boolean) => void
  // onCardPaid runs when a pay link for the package is paid, so the client's
  // packages can be reloaded (the purchase is created by the payment).
  onCardPaid?: () => void
}

// SellPackageSheet records a sale of one of the therapist's packages to this
// client, in two steps: choose the package, then say how it was paid for.
//
// The payment step mirrors the Complete sheet's "How did they pay?", so the
// sale is recorded the same way a session is: cash, card or bank transfer.
export default function SellPackageSheet({ clientId, clientName, card, clientHasEmail = false, carryOverUntil = null, onClose, onSold, onCardPaid }: SellPackageSheetProps) {
  const [carryOverUntilNow, setCarryOverUntilNow] = React.useState(carryOverUntil)
  const canCarryOver = carryOverOpen(carryOverUntilNow)
  const [packages, setPackages] = React.useState<Package[] | null>(null)
  const [loadFailed, setLoadFailed] = React.useState(false)
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [step, setStep] = React.useState<'pick' | 'pay'>('pick')
  const [selling, setSelling] = React.useState(false)
  const [error, setError] = React.useState('')
  const [carriedOver, setCarriedOver] = React.useState(false)
  const [sessionsLeft, setSessionsLeft] = React.useState('')
  const [sessionsError, setSessionsError] = React.useState('')
  const [cardAccount, setCardAccount] = React.useState<CardAccount | null>(null)
  const [payLinkOpen, setPayLinkOpen] = React.useState(false)
  const [chargeOpen, setChargeOpen] = React.useState(false)
  const cardPaid = React.useRef(false)

  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose, { closable: !selling && !payLinkOpen && !chargeOpen })

  // Card options are offered only when card payments are ready. Asked once
  // per sheet open; a failure just hides them.
  React.useEffect(() => {
    let live = true
    api.cardAccount().then((account) => { if (live) setCardAccount(account) }).catch(() => {})
    return () => { live = false }
  }, [])

  // The app reads /v1/me once as it starts, so an import applied while it
  // stayed open would leave the switch hidden until a reload. Asked again
  // per sheet open; a failure keeps what the app had.
  React.useEffect(() => {
    let live = true
    api.me().then((me) => { if (live) setCarryOverUntilNow(me.fresha_carry_over_until ?? null) }).catch(() => {})
    return () => { live = false }
  }, [])

  const load = React.useCallback(() => {
    setPackages(null)
    setLoadFailed(false)
    api
      .listPackages()
      .then(setPackages)
      .catch(() => setLoadFailed(true))
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  // One key per sale per sheet open: a retry after a lost answer replays the
  // first sale instead of selling twice.
  const keys = React.useRef(new SheetKeys()).current

  const selected = packages?.find((pkg) => pkg.id === selectedId) ?? null

  const choose = (pkg: Package) => {
    setSelectedId(pkg.id)
    setSessionsLeft(String(pkg.session_count))
    setSessionsError('')
  }

  const sellCarriedOver = async () => {
    if (!selected || selling) return
    const left = Number(sessionsLeft)
    if (!Number.isInteger(left) || left < 1 || left > selected.session_count) {
      setSessionsError(`Sessions left must be between 1 and ${selected.session_count}.`)
      return
    }
    setSelling(true)
    setError('')
    setSessionsError('')
    const request = `carry:${selected.id}:${left}`
    try {
      onSold(await api.sellCarriedOverPackage(clientId, selected.id, left, keys.key(request)), true)
    } catch (e) {
      keys.failed(request, e)
      if (e instanceof ValidationError && e.fields.sessions_left) setSessionsError(e.fields.sessions_left)
      else if (e instanceof ValidationError && e.fields.carried_over) setError(e.fields.carried_over)
      else setError(saveErrorMessage(e))
      setSelling(false)
    }
  }

  const sell = async (method: PaymentMethod) => {
    if (!selectedId || selling) return
    setSelling(true)
    setError('')
    const request = `sell:${selectedId}:${method}`
    try {
      const purchase = await api.sellPackage(clientId, selectedId, method, keys.key(request))
      onSold(purchase)
    } catch (e) {
      keys.failed(request, e)
      setError(saveErrorMessage(e))
      setSelling(false)
    }
  }

  const sheet = (
    <div
      className="sheet-overlay"
      onClick={() => {
        if (!selling) onClose()
      }}
    >
      <div ref={dialogRef}
        className="sheet-content"
        role="dialog"
        aria-modal="true"
        aria-label={`Sell a package to ${clientName}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-handle"></div>
        <div className="sheet-header">
          <h2>Sell a package</h2>
          <p>{step === 'pay' && selected ? selected.name : 'Choose a package.'}</p>
        </div>

        {step === 'pick' ? (
          loadFailed ? (
            <div className="clients-status" role="alert">
              <p>Couldn’t load your packages. Check your connection and try again.</p>
              <button type="button" className="btn btn-secondary btn-compact" onClick={load}>
                Try again
              </button>
            </div>
          ) : packages === null ? (
            <Loading />
          ) : packages.length === 0 ? (
            <p className="clients-status">No packages set up yet. Add one in Settings → Menu first.</p>
          ) : (
            <ul className="service-list sell-list">
              {packages.map((pkg) => (
                <li key={pkg.id} className="service-item">
                  <input
                    type="radio"
                    name="sell-package"
                    aria-label={pkg.name}
                    checked={selectedId === pkg.id}
                    onChange={() => choose(pkg)}
                  />
                  <button type="button" className="service-item__main" onClick={() => choose(pkg)}>
                    <span className="service-item__name">{pkg.name}</span>
                    <span className="service-item__meta">
                      {pkg.session_count} sessions · {priceOrFree(pkg.price_pence)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : (
          selected && (
            <section className="booking-section">
              <h3 className="booking-label">How did they pay?</h3>
              <div className="booking-actions">
                {canTakeCardPayments(cardAccount) && usableCard(card) && (
                  <button
                    type="button"
                    className="btn btn-primary btn-compact"
                    onClick={() => setChargeOpen(true)}
                    disabled={selling}
                  >
                    Charge {cardLabel(card)} · {formatPrice(selected.price_pence)}
                  </button>
                )}
                {PAYMENT_METHODS.map(({ method, label }) => (
                  <button
                    key={method}
                    type="button"
                    className="btn btn-secondary btn-compact"
                    onClick={() => sell(method)}
                    disabled={selling}
                  >
                    {selling ? 'Recording…' : `${label} · ${formatPrice(selected.price_pence)}`}
                  </button>
                ))}
                {canTakeCardPayments(cardAccount) && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-compact"
                    onClick={() => setPayLinkOpen(true)}
                    disabled={selling}
                  >
                    Card (pay link) · {formatPrice(selected.price_pence)}
                  </button>
                )}
              </div>
            </section>
          )
        )}

        {step === 'pick' && canCarryOver && packages && packages.length > 0 && (
          <section className="sell-carried">
            <label className="settings-row settings-row--switch">
              <input
                type="checkbox"
                role="switch"
                checked={carriedOver}
                disabled={selling}
                onChange={(e) => {
                  setCarriedOver(e.target.checked)
                  setSessionsError('')
                }}
              />
              <span>Carried over from Fresha</span>
            </label>
            {carriedOver && (
              <Field
                id="sessions-left"
                label="Sessions left"
                hint={selected ? `1 to ${selected.session_count}. Nothing is charged.` : 'Choose a package first.'}
                error={sessionsError}
              >
                <input
                  id="sessions-left"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={selected?.session_count}
                  value={sessionsLeft}
                  disabled={!selected || selling}
                  onChange={(e) => setSessionsLeft(e.target.value)}
                />
              </Field>
            )}
          </section>
        )}

        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}

        <div className="sheet-actions">
          {step === 'pick' ? (
            <>
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={carriedOver ? sellCarriedOver : () => setStep('pay')}
                disabled={!selectedId || selling}
              >
                {carriedOver ? (selling ? 'Recording…' : 'Add carried-over package') : 'Continue'}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setError('')
                setStep('pick')
              }}
              disabled={selling}
            >
              Back
            </button>
          )}
        </div>
      </div>
      {chargeOpen && selected && usableCard(card) && (
        <ChargeCardSheet
          target={{ client_id: clientId, package_id: selected.id }}
          amountPence={selected.price_pence}
          card={cardLabel(card)}
          firstName={clientName.split(' ')[0]}
          emailed={clientHasEmail}
          onClose={() => {
            setChargeOpen(false)
            // Paid: the sale is done, so leave the sell flow too.
            if (cardPaid.current) onClose()
          }}
          onPaid={() => {
            cardPaid.current = true
            onCardPaid?.()
          }}
          onSendLink={() => {
            setChargeOpen(false)
            setPayLinkOpen(true)
          }}
        />
      )}
      {payLinkOpen && selected && (
        <PayLinkSheet
          target={{ client_id: clientId, package_id: selected.id }}
          clientHasCard={usableCard(card)}
          onClose={() => {
            setPayLinkOpen(false)
            // Paid: the sale is done, so leave the sell flow too.
            if (cardPaid.current) onClose()
          }}
          onPaid={() => {
            cardPaid.current = true
            onCardPaid?.()
          }}
        />
      )}
    </div>
  )

  // The sheet renders into the app shell rather than into the client profile.
  // The tab bar carries a backdrop-filter, which makes it its own stacking
  // context and paints it over anything nested inside the scrolling screen —
  // no z-index on the overlay can win. Mounted here it sits above the tab bar
  // and the floating button, the same as every other sheet, and its actions
  // stay tappable instead of the nav swallowing the taps.
  const host = typeof document === 'undefined' ? null : document.querySelector('.app-container')
  return host ? createPortal(sheet, host) : sheet
}
